import { toast } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import type { ApiError } from "@/hooks/useApi";
import { useNavigateNextPatient } from "@/hooks/useNavigateNextPatient";
import { trackEvent } from "@/lib/tracking";
import { useUser } from "@/network/useUser";
import type { AxiosError } from "axios";
import { useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { roomActionsInProgressAtom } from "../atoms/roomActions";
import {
  type CloseEncounterError,
  type HandoffEncounterError,
  useCancelEncounter,
  useCloseEncounter,
  useHandoffEncounter,
  useIsEncounterClosable,
  usePauseEncounter,
  useRejectAsyncRequest,
  useStartAmazonClinicalNote,
  useStealEncounter,
} from "./network/useEncounterTransitions";

// Different endpoints return different formats for error messages
function getErrorMessage(
  error: AxiosError<HandoffEncounterError | CloseEncounterError> | ApiError,
) {
  const detail = error.response?.data?.detail;

  if (detail && typeof detail === "object") {
    if (
      "failureDebugText" in detail &&
      typeof detail.failureDebugText === "string"
    ) {
      return detail.failureDebugText;
    }
    if ("debugMessage" in detail && typeof detail.debugMessage === "string") {
      return detail.debugMessage;
    }
  }

  if (typeof detail === "string") return detail;
  return error.message || "Unknown error";
}

export function useRoomOptions({
  encounterId,
  patientId,
  isAmazon,
}: {
  encounterId: string;
  patientId: string;
  isAmazon: boolean;
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const navigateNextPatient = useNavigateNextPatient();
  const navigateAction = () => navigateNextPatient({ patientId, encounterId });

  const { refetch: refetchEncounterClosable } = useIsEncounterClosable({
    patientId,
    encounterId,
    enabled: !isAmazon, // Disable for Amazon visits
  });
  async function isEncounterClosable() {
    const { data } = await refetchEncounterClosable();
    if (!data?.closable) {
      toast.error(data?.reason || "Encounter cannot be closed at this time");
      return false;
    }
    return true;
  }

  // Cancel
  const { mutateAsync: cancel, isPending: isCancelPending } =
    useCancelEncounter({
      encounterId,
      patientId,
      onSuccess: () => {
        toast.success("Successfully cancelled the encounter");
        navigateAction();
      },
      onError: (error) => {
        toast.error(`Failed to cancel the encounter: ${error.message}`);
      },
    });

  // Complete
  const { mutateAsync: complete, isPending: isCompletePending } =
    useCloseEncounter({
      patientId,
      encounterId,
      onMutate: () => {
        trackEvent(TRACKERS.CLOSE_VISIT);
      },
      onSuccess: () => {
        navigateAction();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(`Failed to close the encounter: ${errorMessage}`);
      },
    });

  // Handoff to CA
  const { mutateAsync: handoffCA, isPending: isHandoffCAPending } =
    useHandoffEncounter({
      patientId,
      encounterId,
      onSuccess: () => {
        toast.success("Successfully handed off to Clincian's Assistant");
        navigateAction();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(
          `Failed to handoff to Clincian's Assistant: ${errorMessage}`,
        );
      },
    });

  // Handoff to LC
  const { mutateAsync: handoffLC, isPending: isHandoffLCPending } =
    useHandoffEncounter({
      patientId,
      encounterId,
      onMutate: () => {
        trackEvent(TRACKERS.CLINICIAN_HANDOFF);
      },
      onSuccess: () => {
        toast.success("Successfully handed off to Licensed Clinician");
        navigateAction();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(`Failed to handoff to Licensed Clinician: ${errorMessage}`);
      },
    });

  // Incomplete
  const { mutateAsync: incomplete, isPending: isIncompletePending } =
    useCloseEncounter({
      patientId,
      encounterId,
      onMutate: () => {
        trackEvent(TRACKERS.MARK_VISIT_INCOMPLETE);
      },
      onSuccess: () => {
        toast.success("Successfully marked visit as incomplete");
        navigateAction();
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(`Failed to mark visit as incomplete: ${errorMessage}`);
      },
    });

  // Pause Pending Patient Response
  const { mutateAsync: pause, isPending: isPausePending } = usePauseEncounter({
    encounterId,
    patientId,
    onSuccess: () => {
      toast.success("Successfully marked visit as pending patient response");
      navigateAction();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast.error(
        `Failed to mark visit as pending patient response: ${errorMessage}`,
      );
    },
  });

  // Start Amazon Clinical Note
  const { mutateAsync: clinicalNote, isPending: isClinicalNotePending } =
    useStartAmazonClinicalNote({
      encounterId,
      patientId,
      onSuccess: () => {
        toast.success("Successfully started Amazon clinical note");
      },
      onError: (error) => {
        toast.error(`Failed to start Amazon clinical note: ${error.message}`);
      },
    });

  // Steal
  const { data: me } = useUser();
  const { mutateAsync: steal, isPending: isStealPending } = useStealEncounter({
    encounterId,
    patientId,
    targetPractitionerId: me?.professional?.fhir.id,
    onSuccess: () => {
      toast.success("Patient successfully taken");
      setIsDropdownOpen(false);
    },
    onError: (error) => {
      toast.error(`Failed to take patient: ${error.message}`);
    },
  });

  // Reject Rx Request
  const {
    mutateAsync: rejectAsyncRequest,
    isPending: isRejectAsyncRequestPending,
  } = useRejectAsyncRequest({
    encounterId,
    patientId,
    onSuccess: () => {
      toast.success("Rx request successfully rejected");
      navigateAction();
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);
      toast.error(`Failed to reject Rx request: ${errorMessage}`);
    },
  });

  // Keep track of whether any room actions are pending and store in Jotai atom
  const isAnyActionPending =
    isCancelPending ||
    isClinicalNotePending ||
    isCompletePending ||
    isIncompletePending ||
    isHandoffCAPending ||
    isHandoffLCPending ||
    isPausePending ||
    isStealPending ||
    isRejectAsyncRequestPending;

  const setRoomActionsInProgress = useSetAtom(roomActionsInProgressAtom);
  useEffect(() => {
    setRoomActionsInProgress(isAnyActionPending);

    return () => {
      setRoomActionsInProgress(false);
    };
  }, [isAnyActionPending, setRoomActionsInProgress]);

  return {
    actions: {
      cancel,
      complete,
      clinicalNote,
      handoffCA,
      handoffLC,
      incomplete,
      pause,
      steal,
      rejectAsyncRequest,
    },
    isEncounterClosable,
    isDropdownOpen,
    pending: {
      anyAction: isAnyActionPending,
      cancel: isCancelPending,
      clinicalNote: isClinicalNotePending,
      complete: isCompletePending,
      handoffCA: isHandoffCAPending,
      handoffLC: isHandoffLCPending,
      incomplete: isIncompletePending,
      pause: isPausePending,
      rejectAsyncRequest: isRejectAsyncRequestPending,
      steal: isStealPending,
    },
    setIsDropdownOpen,
  };
}
